import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import MikeColors from '@mike/mike-shared-frontend/mike-shared-styles/mike-colors'
import MikeButton from '@mike/mike-shared-frontend/mike-button'
import ProjectsTree from '../ProjectsTree'
import CircularProgress from '@mui/material/CircularProgress'

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1)
}))

const StyledActionButton = styled(MikeButton)(({ theme }) => ({
  margin: theme.spacing(1)
}))

const StyledDialogContent = styled(DialogContent)({
  maxHeight: 542,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  scrollbarColor: MikeColors.DARKGREY_DEFAULT + ' ' + MikeColors.WHITE,
  '&::-webkit-scrollbar': {
    width: 24,
    borderRadius: '4px 4px'
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '4px 4px',
    backgroundColor: MikeColors.MEDIUMGREY_LIGHT,
    borderRight: '8px solid ' + MikeColors.WHITE,
    borderLeft: '12px solid ' + MikeColors.WHITE
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '4px 4px',
    backgroundColor: MikeColors.DARKGREY_DEFAULT,
    borderRight: '8px solid ' + MikeColors.WHITE,
    borderLeft: '12px solid ' + MikeColors.WHITE,
    '&:hover': {
      borderRight: '8px solid ' + MikeColors.WHITE,
      borderLeft: '12px solid ' + MikeColors.WHITE
    }
  }
})

function ProjectsTreeDialog({
  onExpand,
  open,
  onClose,
  onMove,
  projectTree,
  selectedIds,
  disabledIds,
  loadingInitialTree,
  parentId
}) {
  const intl = useIntl()

  const [selectedProjectId, setSelectedProjectId] = React.useState(null)

  const selectedTooltip = intl.formatMessage({
    id: 'projectTree.tooltip.selected'
  })

  const parentTooltip = intl.formatMessage({ id: 'projectTree.tooltip.parent' })

  const tooltip =
    selectedIds?.length > 1
      ? intl.formatMessage({ id: 'projectTree.tooltip.plural' })
      : intl.formatMessage({ id: 'projectTree.tooltip.singular' })

  const title =
    selectedIds?.length > 1
      ? intl.formatMessage({ id: 'projectTreeDialog.heading.title.plural' })
      : intl.formatMessage({ id: 'projectTreeDialog.heading.title.singular' })

  const handleMove = () => {
    onMove(selectedProjectId)
  }

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="mike-dataadmin-move-dialog-title"
      open={open}
      sx={{ '& .MuiDialog-paper': { minWidth: 560, maxHeight: 590 } }}
    >
      <DialogTitle id="mike-dataadmin-move-dialog-title">
        {title}
        <StyledCloseButton onClick={onClose} size="large">
          <CloseIcon />
        </StyledCloseButton>
      </DialogTitle>
      <StyledDialogContent>
        {loadingInitialTree ? (
          <CircularProgress />
        ) : (
          <ProjectsTree
            onExpand={onExpand}
            onSelect={setSelectedProjectId}
            projectTree={projectTree}
            selectedIds={selectedIds}
            disabledIds={disabledIds}
            tooltip={tooltip}
            selectedItemTooltip={selectedTooltip}
            parentIdTooltip={parentTooltip}
            parentId={parentId}
          />
        )}
      </StyledDialogContent>
      <DialogActions>
        <StyledActionButton onClick={onClose} buttontype="text">
          Cancel
        </StyledActionButton>
        <StyledActionButton
          disabled={selectedProjectId === null}
          onClick={handleMove}
          buttontype="primary"
        >
          {intl.formatMessage({ id: 'projectTreeDialog.moveButton.label' })}
        </StyledActionButton>
      </DialogActions>
    </Dialog>
  )
}

ProjectsTreeDialog.propTypes = {
  onExpand: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectTree: PropTypes.object,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabledIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  loadingInitialTree: PropTypes.bool.isRequired,
  parentId: PropTypes.string
}

export default ProjectsTreeDialog
